(() => {
  if (customElements.get('zuzu-socks-bundle')) return;

  class ZuZuSocksBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      try {
        this.source = JSON.parse(this.querySelector('[data-bundle-source]').textContent);
        const names = this.source.options.map(name => String(name).trim().toLowerCase());
        this.sizeIndex = names.findIndex(name => ['size', 'מידה', 'größe', 'taille', 'talla', 'taglia', 'tamanho'].includes(name));
        this.colorIndex = names.findIndex(name => ['color', 'colour', 'צבע', 'farbe', 'couleur', 'colore', 'cor'].includes(name));
        if (names.length !== 2 || this.sizeIndex < 0 || this.colorIndex < 0 || !this.source.variants.length) return;
        this.tiers = (this.source.tiers || []).filter(tier => tier.enabled && [1, 2, 3, 5].includes(tier.quantity));
        if (!this.tiers.length) return;
        this.strings = this.source.strings;
        this.variants = this.source.variants;
        this.sizes = [...new Set(this.variants.map(variant => variant.options[this.sizeIndex]))];
        this.colors = [...new Set(this.variants.map(variant => variant.options[this.colorIndex]))];
        this.swatches = new Map();
        String(this.source.swatches || '').split('\n').forEach(line => {
          const separator = line.indexOf(':');
          const color = line.slice(separator + 1).trim();
          if (separator > 0 && CSS.supports('color', color)) this.swatches.set(line.slice(0, separator).trim().toLowerCase(), color);
        });
        this.money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency });
        this.count = this.source.startUnselected ? 0 : this.tiers[0].quantity;
        this.rows = Array.from({ length: this.count }, () => ({ size: '', color: '' }));
        this.form = this.querySelector('[data-bundle-form]');
        this.rowContainer = this.querySelector('[data-bundle-rows]');
        this.rowsHost = this.querySelector('[data-bundle-rows-host]');
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.submitText = this.querySelector('[data-bundle-submit-text]');
        this.status = this.querySelector('[data-bundle-status]');
        this.review = this.querySelector('[data-bundle-review]');
        this.controller = new AbortController();
        this.form.addEventListener('change', event => this.onChange(event), { signal: this.controller.signal });
        this.form.addEventListener('submit', event => this.onSubmit(event), { signal: this.controller.signal });
        const info = this.closest('product-info');
        const formId = info?.getAttribute('form');
        const nativeForm = formId ? document.getElementById(formId) : null;
        this.nativeControls = [info?.querySelector('variant-picker'), nativeForm?.closest('.product-form-wrapper'), document.getElementById(`ProductStickyForm-${this.dataset.sectionId}-${this.dataset.productId}`)].filter(Boolean).map(element => ({ element, hidden: element.hidden }));
        this.price = document.getElementById(`Price-${this.dataset.sectionId}-${this.dataset.productId}`)?.querySelector('.price__regular');
        this.originalPrice = this.price?.textContent;
        this.render();
        this.nativeControls.forEach(({ element }) => { element.setAttribute('data-zuzu-bundle-replaced', ''); element.hidden = true; });
        this.hidden = false;
        this.initialized = true;
      } catch (error) {
        this.controller?.abort();
        this.hidden = true;
        console.error('Socks purchase block could not initialize', error);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.nativeControls?.forEach(({ element, hidden }) => { element.hidden = hidden; element.removeAttribute('data-zuzu-bundle-replaced'); });
      if (this.price) this.price.textContent = this.originalPrice;
      this.initialized = false;
    }

    format(cents) { return this.money.format(cents / 100); }
    activeRows() { return this.rows.slice(0, this.count); }
    resolve(row, variants = this.variants) {
      return variants.find(variant => variant.options[this.sizeIndex] === row?.size && variant.options[this.colorIndex] === row?.color);
    }
    baseTotal(count = this.count) {
      let total = 0;
      for (let i = 0; i < count; i++) {
        const row = this.rows[i] || this.rows[0];
        const matching = this.variants.find(variant => variant.available && (!row?.size || variant.options[this.sizeIndex] === row.size) && (!row?.color || variant.options[this.colorIndex] === row.color));
        total += Number((this.resolve(row) || matching || this.variants.find(variant => variant.available) || this.variants[0]).price);
      }
      return total;
    }
    rate(count) {
      const rate = Number(this.source.discounts?.[count] || 0);
      return this.source.discountsEnabled && Number.isFinite(rate) ? Math.min(100, Math.max(0, rate)) : 0;
    }
    total(count = this.count) {
      const base = this.baseTotal(count);
      if (this.source.discountTypes?.[count] !== 'amount') return Math.round(base * (1 - this.rate(count) / 100));
      if (!this.source.discountsEnabled || this.dataset.currency !== this.source.fixedDiscountCurrency) return base;
      const amount = Number(this.source.fixedDiscounts?.[count] || 0);
      return Number.isFinite(amount) ? Math.max(0, base - Math.max(0, Math.round(amount * 100))) : base;
    }
    savingPercent(count) {
      if (this.source.discountTypes?.[count] !== 'amount') return this.rate(count);
      const base = this.baseTotal(count);
      return base > 0 ? Math.round(100 * (base - this.total(count)) / base) : 0;
    }
    available(row, key, value) {
      const next = { ...row, [key]: value };
      return this.variants.some(variant => variant.available && (!next.size || variant.options[this.sizeIndex] === next.size) && (!next.color || variant.options[this.colorIndex] === next.color));
    }

    onChange(event) {
      if (this.busy) return;
      if (!this.uncertain) { this.status.hidden = true; this.review.hidden = true; }
      if (event.target.matches('[data-bundle-pack]')) {
        const count = Number(event.target.value);
        if (!this.tiers.some(tier => tier.quantity === count)) return;
        const previous = this.count;
        this.count = count;
        while (this.rows.length < count) this.rows.push({ size: '', color: '' });
        for (let index = previous; index < count; index++) {
          if (!this.rows[index].size) this.rows[index].size = this.rows[0].size;
          if (this.source.inheritColor && !this.rows[index].color) this.rows[index].color = this.rows[0].color;
        }
      } else {
        const index = Number(event.target.dataset.row);
        if (!Number.isInteger(index) || !this.rows[index]) return;
        const key = event.target.matches('[data-pair-size]') ? 'size' : event.target.matches('[data-pair-color]') ? 'color' : null;
        if (!key) return;
        this.rows[index][key] = event.target.value;
        if (index === 0 && this.rows[0][key] && (key === 'size' || this.source.inheritColor)) {
          this.activeRows().forEach((row, i) => { if (i > 0 && !row[key]) row[key] = this.rows[0][key]; });
        }
      }
      this.render();
    }

    render() {
      const focus = document.activeElement;
      const focusId = this.contains(focus) ? focus.id : null;
      const template = this.querySelector('[data-bundle-row-template]');
      const fragment = document.createDocumentFragment();
      if (this.count && this.source.rowsHeading) {
        const heading = document.createElement('p'); heading.className = 'zuzu-bundle-rows-heading'; heading.textContent = this.source.rowsHeading; fragment.append(heading);
      }
      const columns = document.createElement('div'); columns.className = 'zuzu-bundle-columns'; columns.setAttribute('aria-hidden', 'true');
      ['', this.strings.size, this.strings.color].forEach(text => { const span = document.createElement('span'); span.textContent = text; columns.append(span); });
      if (this.count) fragment.append(columns);
      this.activeRows().forEach((row, index) => {
        const node = template.content.firstElementChild.cloneNode(true);
        const pair = this.strings.pair.replace('__number__', index + 1);
        node.querySelector('[data-pair-label]').textContent = pair;
        const size = node.querySelector('[data-pair-size]');
        size.id = `${this.id}-size-${index}`; size.dataset.row = index; size.name = `size-${this.id}-${index}`;
        const sizeLabel = node.querySelector('[data-size-label]'); sizeLabel.htmlFor = size.id; sizeLabel.textContent = `${this.strings.size} — ${pair}`;
        size.append(new Option(this.strings.chooseSize, ''));
        this.sizes.forEach(value => { const option = new Option(value, value); option.disabled = !this.available(row, 'size', value); size.append(option); });
        size.value = row.size; size.disabled = Boolean(this.busy);
        const color = node.querySelector('[data-pair-color]');
        color.id = `${this.id}-color-${index}`; color.dataset.row = index; color.name = `color-${this.id}-${index}`;
        const colorLabel = node.querySelector('[data-color-label]'); colorLabel.htmlFor = color.id; colorLabel.textContent = `${this.strings.color} — ${pair}`;
        color.append(new Option(this.strings.chooseColor, ''));
        this.colors.forEach(value => { const option = new Option(value, value); option.disabled = !this.available(row, 'color', value); color.append(option); });
        color.value = row.color; color.disabled = Boolean(this.busy);
        const dot = node.querySelector('[data-color-dot]');
        dot.classList.toggle('is-empty', !row.color); dot.style.setProperty('--bundle-color', this.swatches.get(row.color.toLowerCase()) || 'transparent');
        if (row.size && row.color && !this.resolve(row)?.available) { const error = node.querySelector('[data-pair-error]'); error.textContent = this.strings.unavailable; error.hidden = false; }
        fragment.append(node);
      });
      this.rowContainer.replaceChildren(fragment);
      const vertical = this.dataset.layout === 'vertical';
      this.rowsHost.hidden = vertical || !this.count;
      this.querySelectorAll('[data-pack-rows]').forEach(slot => { slot.hidden = !vertical || Number(slot.dataset.packRows) !== this.count; });
      (vertical && this.count ? this.querySelector(`[data-pack-rows="${this.count}"]`) : this.rowsHost).append(this.rowContainer);
      this.querySelectorAll('[data-bundle-pack]').forEach(input => { input.checked = Number(input.value) === this.count; input.disabled = Boolean(this.busy); });
      this.querySelectorAll('[data-pack-card]').forEach(card => { card.classList.toggle('is-selected', Number(card.dataset.packCard) === this.count); });
      this.tiers.forEach(({ quantity }) => {
        const base = this.baseTotal(quantity), total = this.total(quantity), savings = base - total;
        const price = this.querySelector(`[data-pack-price="${quantity}"]`);
        price.textContent = this.format(total);
        let cardUnit = this.querySelector(`[data-pack-unit="${quantity}"]`);
        if (!cardUnit) {
          cardUnit = document.createElement('span');
          cardUnit.className = 'zuzu-bundle-card-unit';
          cardUnit.dataset.packUnit = quantity;
          price.parentElement.append(cardUnit);
        }
        cardUnit.hidden = !vertical || !this.source.showUnitPrice;
        cardUnit.textContent = this.strings.unitPrice.replace('__price__', this.format(total / quantity));
        const compare = this.querySelector(`[data-pack-compare="${quantity}"]`); compare.textContent = this.format(base); compare.hidden = !this.source.showCompare || savings <= 0;
        const number = this.source.savingsFormat === 'percent' ? `${this.savingPercent(quantity)}%` : this.format(savings);
        const label = [this.source.savingsLabel, number].filter(Boolean).join(' ');
        ['data-pack-saving', 'data-pack-saving-bottom'].forEach(attribute => { const badge = this.querySelector(`[${attribute}="${quantity}"]`); badge.textContent = label; badge.hidden = savings <= 0; });
      });
      const total = this.total();
      const unit = this.querySelector('[data-bundle-unit]'); unit.hidden = vertical || !this.source.showUnitPrice || this.count < 2; unit.textContent = this.count ? this.strings.unitPrice.replace('__price__', this.format(total / this.count)) : '';
      const valid = this.count > 0 && this.activeRows().every(row => row.size && row.color && this.resolve(row)?.available);
      this.submitButton.disabled = Boolean(this.busy || this.uncertain || !valid);
      const add = this.count === 1 ? this.strings.addPair : this.strings.addPairs.replace('__number__', this.count);
      this.submitText.textContent = this.busy ? this.strings.adding : !this.count ? this.strings.choosePack : valid ? `${add} · ${this.format(total)}` : this.strings.chooseOptions;
      if (focusId && !this.busy) document.getElementById(focusId)?.focus({ preventScroll: true });
    }

    setStatus(message, review = false, announceOnly = false) {
      this.status.textContent = message;
      this.status.classList.toggle('sr-only', announceOnly);
      this.status.hidden = false;
      this.review.hidden = !review;
    }

    request(url, options = {}) {
      return fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout?.(20000), ...options });
    }

    async getCart() {
      const response = await this.request(this.dataset.cartUrl, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(this.strings.uncertain);
      return response.json();
    }

    refreshCart(cart, sections, open = false, added) {
      if (sections) cart.sections = sections;
      if (window.theme?.pubsub) theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'socks-bundle-builder', cart });
      if (added) document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: added } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open } }));
      if (open) document.querySelector('cart-drawer')?.show?.(this.submitButton);
      this.querySelector('[data-bundle-cart-evidence]')?.remove();
      const evidence = document.createElement('script');
      evidence.type = 'application/json';
      evidence.dataset.bundleCartEvidence = '';
      evidence.textContent = JSON.stringify({ currency: cart.currency, item_count: cart.item_count, items: cart.items.filter(item => item.product_id === Number(this.dataset.productId)).map(item => ({ id: item.variant_id, sku: item.sku, quantity: item.quantity, properties: item.properties, final_line_price: item.final_line_price })), total_price: cart.total_price });
      this.append(evidence);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy || this.uncertain || !this.count || this.activeRows().some(row => !row.size || !row.color || !this.resolve(row)?.available)) return;
      this.busy = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.form.querySelectorAll('input, select').forEach(input => { input.disabled = true; });
      this.render();
      let attempted = false;
      let bundleId;
      let responseBody;
      try {
        const response = await this.request(this.dataset.productUrl, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(this.strings.error);
        const fresh = await response.json();
        this.variants = fresh.variants;
        const grouped = new Map();
        this.activeRows().forEach(row => {
          const variant = this.resolve(row);
          if (!variant?.available) throw new Error(this.strings.unavailable);
          grouped.set(variant.id, (grouped.get(variant.id) || 0) + 1);
        });
        bundleId = `socks-${crypto.randomUUID()}`;
        const items = [...grouped].map(([id, quantity]) => ({ id, quantity, properties: { _bundle_id: bundleId, _bundle_size: String(this.count), _bundle_type: 'merino-tabi' } }));
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
        attempted = true;
        const add = await this.request(this.dataset.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items, sections: sections.slice(0, 5), sections_url: window.location.pathname }) });
        responseBody = await add.json();
        const cart = await this.getCart();
        const actual = cart.items.filter(item => item.properties?._bundle_id === bundleId);
        const complete = actual.reduce((sum, item) => sum + item.quantity, 0) === this.count && [...grouped].every(([id, quantity]) => actual.filter(item => item.variant_id === id).reduce((sum, item) => sum + item.quantity, 0) === quantity);
        this.refreshCart(cart, responseBody.sections, complete, complete ? responseBody : null);
        if (!complete) {
          this.uncertain = actual.length > 0;
          this.setStatus(actual.length ? this.strings.partial : responseBody.description || this.strings.error, actual.length > 0);
        } else {
          this.setStatus(this.strings.added, false, true);
        }
      } catch (error) {
        if (attempted) {
          try {
            const cart = await this.getCart();
            const ownLines = cart.items.filter(item => item.properties?._bundle_id === bundleId);
            this.refreshCart(cart, responseBody?.sections, false);
            this.uncertain = true;
            this.setStatus(ownLines.length ? this.strings.partial : this.strings.uncertain, true);
          } catch {
            this.uncertain = true;
            this.setStatus(this.strings.uncertain, true);
          }
        } else this.setStatus(error.message === this.strings.unavailable ? this.strings.unavailable : this.strings.error);
      } finally {
        this.busy = false;
        this.submitButton.removeAttribute('aria-busy');
        this.form.querySelectorAll('[data-bundle-pack]').forEach(input => { input.disabled = false; });
        this.render();
      }
    }
  }

  customElements.define('zuzu-socks-bundle', ZuZuSocksBundle);
})();
